import React from 'react';
import styles from './elemGame.module.css';
import defaultPic from '../../assets/avatar.png';
import { textToMathjax } from '../../utils/mathjaxUtils';
import { motion, TargetAndTransition, useAnimation } from "framer-motion";
import Card from '@mui/material/Card';
import CardMedia from '@mui/material/CardMedia';
import CardContent from '@mui/material/CardContent';
import ReactSpeedometer from "react-d3-speedometer";
import TimeUpDialog from '../../components/Dialogs/TimeUpDialog';
import Timer from '../../components/Timer';
import CheckCircleOutlineSharpIcon from '@mui/icons-material/CheckCircleOutlineSharp';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import TeX from '@matejmazur/react-katex';
import { ChallengeMode, QuestionType } from '../../types/enums';
import { getPrompt } from '../../utils/questionUtils';
import GameLayout from '../../components/GameLayout';
import logo from '../../assets/mtc logo.png';
import SkipBattery from '../../components/SkipBattery';
import PerformanceTimeline from '../../components/PerformanceTimeline';
import { Question } from '../../types/Question';
import { Round } from '../../types/Round';
import { useGameSocket } from '../../providers/gameSocket.provider';
// import { useRecorderPermission } from '../../hooks/useRecorderPermission';
import RecordRTC from 'recordrtc';
import WaitingForHost from '../../components/WaitingForHostLoader';
import { CloudinaryClient } from '../../services/CloudinaryClient';
import ToastContext from '../../contexts/ToastContext';
import { parseError } from '../../utils/errorUtils';
import { backendClient } from '../../services/MtcBackendClient';
import { FinishRoundPayload } from '../../services/MtcBackendClient/types/requests/game.request';
import { useNavigate } from 'react-router-dom';
import clickSfx from '../../assets/sounds/click.mp3';
import { duration } from '@mui/material';
import { GameRoundPayload } from '../../types/socket.types';
// import ScreenRecorder from '../../components/ScreenRecorder';
// import { useReactMediaRecorder } from "react-media-recorder-2";

const CHUNK_SIZE = 100 * 1024; // 100 KB (adjust as needed)
const clickSound = new Audio(clickSfx);


export interface ElemGameScreenProps{
    questions: Question[];
    round: Round;
    onGoBack?: ()=> void;
    recorder?: RecordRTC;
    code: string
}

const ElemGameScreen: React.FC<ElemGameScreenProps> = ({
    questions, round, onGoBack, code
})=>{

    // Ref
    let isRendered = React.useRef<boolean>(false);

    // Contexts
    const { openError } = React.useContext(ToastContext)
    
    // Timer delay
    // const timeDelay = 100;

    // Question
    // const quest = textToMathjax('3 x 7 = ?');

    // Refs
    const scoreRef = React.useRef(0);
    const questionsAnswered = React.useRef(0);
    const questionsSkipped = React.useRef(0);
    const answeredCorrect = React.useRef(0);
    const keyPressed = React.useRef<'right'|'wrong'>()
    const countRef = React.useRef(0);
    // let screenRecorder = React.useRef<ScreenRecorder | null>(null);

    


    // Hooks
    const rightControls = useAnimation();
    const countControls = useAnimation();
    const controls = useAnimation();
    const qControl = useAnimation();
    const { gameSocket } = useGameSocket();
    const navigate = useNavigate()

    const handleVideoProcessing = async (blob: Blob)=>{
        setLoading(true)

        if (round?.mode === ChallengeMode.FIXED_TIME) {
            gameSocket?.emit('stop-timer', round.id)
        }

        try {
            console.log("Blob data type: ", blob?.type)


            if (blob) {
                const totalSize = blob.size;
                let offset = 0;

                const fileName = `${round?.player?.firstname}_${round?.player?.id}_${round.id}_.webm`

                while (offset < totalSize) {
                    const chunk = blob.slice(offset, offset + CHUNK_SIZE);
                    gameSocket?.emit('video-chunk', { round, chunk, fileName, end: false });
                    offset += CHUNK_SIZE;
                }

                gameSocket?.emit('video-chunk', { round, chunk: '', fileName, end: true });
            }
            else{
                alert('ROUND WAS NOT RECORDED');
            }
    
        } catch (error) {
            alert('AN ERROR OCCURED WHILE UPLOADING');
            console.log(error);
            openError(parseError(error as any))
        }
    }
    // const { 
    //     startRecording, stopRecording, 
    // } = useReactMediaRecorder({ 
    //     audio: true, screen: true, 
    //     onStop: (url, blob)=> handleVideoProcessing(blob),
    //     blobPropertyBag:{
    //         type: 'video/webm'
    //     } 
    // });
    // const recorder = useRecorderPermission('video');

    // State
    const [background, setBackground] = React.useState<string>("#00EB96");
    const [metricValue, setMetric] = React.useState<number>(0);
    const [open, setOpen] = React.useState<boolean>(false);
    const [qIndex, setQIndex] = React.useState<number>(0);
    const [correct, setCorrect] = React.useState<boolean>(false);
    const [skipCount, setSkipCount] = React.useState<number>(0);
    const [stopMeter, setStopMeter] = React.useState<boolean>(false);
    const [stopClock, setStopClock] = React.useState<boolean>(false);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [soundLoaded, setSoundLoaded] = React.useState<boolean>(false) 


    // Sound hook
    // const [play] = useSound(clickSfx, {
    //     soundEnabled: true,
    //     onload: () => setSoundLoaded(true)
    // });

    // Memoizee value
    const maxTime = React.useMemo(()=>{
        return round?.rule?.maxTime;
    }, [])

    const memoizedStopClock = React.useMemo(()=>{
        return stopClock;
    }, [stopClock])

    // Animations
    let flash: TargetAndTransition = {
        background: [background, '#914DF3'],
        transition:{
            duration: 0.5
        }
    }
    let tickFlash = React.useRef<TargetAndTransition>({
        opacity: [1, 0],
        transition:{
            duration: 0.5
        }
    })
    let countdownIndicator: TargetAndTransition = {
        width: '0%',
        transition: { duration: maxTime, ease: 'linear' }
    }
    // Animations and Refs
    let animateCorrect = React.useRef<TargetAndTransition>({
        backgroundColor:['#683da6', '#00EB96', '#683da6'],
        transition:{
            duration: 0.3
        }
    })
    let animateWrong = React.useRef<TargetAndTransition>({
        backgroundColor:['#683da6', '#CE3427', '#683da6'],
        transition:{
            duration: 0.3
        }
    });
    let animateQuestion = React.useRef<TargetAndTransition>({
       scale: [0, 1],
       transition:{
           duration: 0.5,
           stiffness: 125
        }
    })

    // Offset calculator
    const calculateOffset = ()=>{
        countRef.current += 1;
    }

    // Handlers
    const handleClose = ()=>{
        onGoBack && onGoBack()
    }

    const stopRecordingForSuddenDeath = (suddenDeath?: boolean)=>{
        // recorder?.stopRecording(function() {
        //     let blob = recorder.getBlob();
        //     let myBlob = new Blob([blob], {
        //         type: 'video/webm;codecs=vp9'
        //     })
        //     // invokeSaveAsDialog(myBlob);
        //     handleVideoProcessing(myBlob);
        // });
        const payload: GameRoundPayload = {
            score: suddenDeath ? scoreRef.current+ (scoreRef.current / 2) : scoreRef.current,
            questionsAnswered: questionsAnswered.current,
            answeredCorrect: answeredCorrect.current,
            answeredWrong: questionsAnswered.current - answeredCorrect.current,
            questionsSkipped: questionsSkipped.current,
            actualTime: countRef.current,
            code
        }
        gameSocket?.emit('response-round', payload);
        navigate(`/${code}/review/${round.id}`);
    }
    
    const handleOnZero = async ()=>{
        gameSocket?.emit('stop-timer', round.id)
        setStopMeter(true);
        // unsubscribeToKeyDown();
        console.log(scoreRef.current);
        //navigate(`/review/${round.id}`)
        stopRecordingForSuddenDeath(round.mode === ChallengeMode.SUDDEN_DEATH);
    }
    const handleDialogClose = ()=>{
        setOpen(false);
    }
    const flashRight = ()=>{
        controls?.start(animateCorrect.current);
        rightControls?.start(tickFlash.current);
        setCorrect(true);
    }
    const flashWrong = ()=>{
        controls?.start(animateWrong.current);
        rightControls?.start(tickFlash.current);
        setCorrect(false);
    }

    const nextQuestion = ()=>{
        setQIndex((prevIndex)=>{
            if (questions) {
                if (prevIndex === (questions.length - 1)) {
                    return prevIndex
                }
            }

            return prevIndex + 1;
        })
        qControl.start(animateQuestion.current);
    }

    const handleWrongForSuddenDeath = ()=>{
        keyPressed.current = 'wrong';
        questionsAnswered.current += 1;
        //setBackground("#CE3427");
        flashWrong();
        let qPerSecond = answeredCorrect.current/countRef.current;
        setMetric(qPerSecond);

        if (countRef.current < (0.25 *(maxTime || 0))) {
            // scoreRef.current -= (rule?.minusPoints || 0);
            scoreRef.current = 0;
            gameSocket?.emit('stop-timer', round.id)
            gameSocket?.emit('stop-clock-init', round.id);
            setStopClock(true);
            stopRecordingForSuddenDeath()
        }
        else if (countRef.current < (0.5 *(maxTime || 0))){
            scoreRef.current = scoreRef.current / 2;
            gameSocket?.emit('stop-timer', round.id)
            gameSocket?.emit('stop-clock-init', round.id);
            setStopClock(true);
            stopRecordingForSuddenDeath()
        }
        else if (countRef.current < (0.75 *(maxTime || 0))){
            gameSocket?.emit('stop-timer', round.id)
            gameSocket?.emit('stop-clock-init', round.id);
            setStopClock(true);
            stopRecordingForSuddenDeath()
        }
        else{
            scoreRef.current = scoreRef.current + (scoreRef.current / 2);
            gameSocket?.emit('stop-timer', round.id)
            gameSocket?.emit('stop-clock-init', round.id);
            setStopClock(true);
            stopRecordingForSuddenDeath()
            // nextQuestion();
        }
    }

    const handleCorrectForSuddenDeath = ()=>{
        keyPressed.current = 'right';
        questionsAnswered.current += 1;
        answeredCorrect.current += 1;
        scoreRef.current += (round?.rule?.plusPoints || 0);
        flashRight();
        let qPerSecond = answeredCorrect.current/countRef.current;
        setMetric(qPerSecond);

        // let allQuestionsAnsweredCorrectly: boolean = answeredCorrect.current === questionsAnswered.current;

        // if (countRef.current > 15 && countRef.current <= 30 && !allQuestionsAnsweredCorrectly) {
        //     scoreRef.current += (round?.rule?.plusPoints ? round.rule.plusPoints/2 : 0);
        // }
        // else if (countRef.current >= 60) {
        //     scoreRef.current += (round?.rule?.plusPoints ? round?.rule.plusPoints * 1.5 : 0);
        // }
        // else{
        //     scoreRef.current += (round?.rule?.plusPoints || 0);
        // }
        nextQuestion();
    }

    const handleWrong = ()=>{
        keyPressed.current = 'wrong';
        questionsAnswered.current += 1;
        scoreRef.current -= (round?.rule?.minusPoints || 0);
        setBackground("#CE3427");
        flashWrong();
        // setMetric((prevMetric)=>{
        //     if (prevMetric === 0) {
        //         return prevMetric
        //     }
        //     else{
        //         return prevMetric -1
        //     }
        // });
        let qPerSecond = answeredCorrect.current/countRef.current;
        setMetric(qPerSecond);
        nextQuestion();
    }

    const handleCorrect = ()=>{
        keyPressed.current = 'right';
        questionsAnswered.current += 1;
        answeredCorrect.current += 1;
        scoreRef.current += (round?.rule?.plusPoints || 0);
        //setBackground("#00EB96");
        flashRight();
        let qPerSecond = answeredCorrect.current/countRef.current;
        setMetric(qPerSecond);
        //setMetric((prevMetric)=>(prevMetric + 2));
        nextQuestion();
    }

    // Subs and unsubs
    const subscribeToKeyDown = ()=>{
        gameSocket?.on('correct', (event: any)=>{
            round?.mode === ChallengeMode.FIXED_TIME ? handleCorrect() : handleCorrectForSuddenDeath();
        });
        gameSocket?.on('wrong', (event: any)=>{
            round?.mode === ChallengeMode.FIXED_TIME ? handleWrong() : handleWrongForSuddenDeath();
        })
        
        gameSocket?.on('prev', (event: any)=>{
            if (keyPressed.current === 'right') {
                keyPressed.current = undefined;
                answeredCorrect.current -= 1;
                scoreRef.current -= (round?.rule?.plusPoints || 0);
                setBackground("#914DF3");
                setQIndex((prevIndex)=>{
                    if (prevIndex === 0) {
                        return prevIndex
                    }

                    return prevIndex - 1;
                })
                questionsAnswered.current -= 1;
            }
            if (keyPressed.current === 'wrong') {
                keyPressed.current = undefined;
                scoreRef.current += (round?.rule?.minusPoints || 0);
                setBackground("#914DF3");
                setQIndex((prevIndex)=>{
                    if (prevIndex === 0) {
                        return prevIndex
                    }

                    return prevIndex - 1;
                })
                questionsAnswered.current -= 1;
            }    
        })

        gameSocket?.on('skip', (event: any)=>{
            setBackground("#914DF3");
            if (round?.rule?.maxSkip && round?.mode === ChallengeMode.FIXED_TIME) {
                if (skipCount < round?.rule.maxSkip) {
                    questionsSkipped.current += 1;
                    setSkipCount((prevCount)=> prevCount + 1);
                    nextQuestion();
                }
            }
        })

        gameSocket?.on('next', (event: any)=>{
            console.log('Next called');
            // history.push('/review');
        })
    }

    // Effect
    React.useEffect(()=>{
        if (!isRendered.current) {
            subscribeToKeyDown();
            // (async ()=>{
            //     recorder?.startRecording();
            //     const sleep = (m: any) => new Promise(r => setTimeout(r, m));
            //     await sleep((round.rule?.maxTime || 0) * 1000 + 1000);
            //     recorder?.stopRecording(function() {
            //         let blob = recorder.getBlob();
            //         let myBlob = new Blob([blob], {
            //             type: 'video/webm; codecs=vp9'
            //         })
            //         // invokeSaveAsDialog(myBlob);
            //         handleVideoProcessing(myBlob);
            //     });
            // })()

            gameSocket?.emit('start-timer', { duration: round.rule?.maxTime || 0, roundId: round.id });

            gameSocket?.on("time-count", (milliseconds: number)=>{
                const seconds = Number.parseInt((milliseconds/1000).toFixed(0))
                const nextCount = (round.rule?.maxTime || 0) - seconds;

                // If next progression is by 1
                if ((nextCount - countRef.current) === 1) {
                    countRef.current = nextCount;
                }
            })
            
            isRendered.current = true
        }
    
    }, []);
    
    React.useEffect(()=>{
        qControl.start(animateQuestion.current);

        let countInterval = setInterval(()=>{
            if (countRef.current > ((maxTime || 0) - 7) && countRef.current < (maxTime || 0)) {
                clickSound.play()
            }
        }, 1000);

        return ()=> clearInterval(countInterval);
    },[])

    React.useEffect(()=>{
        countControls?.start(countdownIndicator);
        let metricInterval = setInterval(()=>{
            // setMetric((prevMetric)=>{
            //     if (prevMetric === 0) {
            //         return prevMetric
            //     }
            //     else{
            //         return prevMetric -1
            //     }
            // });
            let qPerSecond = answeredCorrect.current/countRef.current;
            setMetric(qPerSecond);
        }, 1000);

        if (stopMeter) {
            clearInterval(metricInterval);
            setMetric(0);
        }

        return ()=> clearInterval(metricInterval)
    },[stopMeter]);

    const memoizedOnZero = React.useCallback(handleOnZero, []);

    // Configs and Defaults
    const config = {
        loader: { load: ["[tex]/html", "output/chtml"] },
        tex: {
            packages: { "[+]": ["html"] },
            inlineMath: [["$", "$"]],
            displayMath: [["$$", "$$"]]
        }
    };
    
    // Elements
    const timerIndicator = (
        <div className={styles.indicator}>
            <motion.div className={styles.indicatorStrip}
                initial={{ width: '100%' }}
                animate={countControls}
            >
            </motion.div>
        </div>
    )

    const mtclogo = (
        <div className={styles.logo}>
            <img src={logo} alt="MTC logo" />
        </div>
    );

    const playerInfo = (
        <div className={styles.infoArea}>

            {/** Logo */}
            { mtclogo }

            {/** Category */}
            <div className={styles.category}>
                <div className={styles.timerHeader}>
                    <h4>Round</h4>
                </div>
                <div className={styles.categoryText}>
                    <p>{ round?.name }</p>
                </div>
            </div>

            {/** Timer */}
            <div className={styles.timer}>
                <div className={styles.timerHeader}>
                    <h4>Timer</h4>
                    { timerIndicator }
                </div>
                <Timer 
                    startCount={maxTime} 
                    onZero={memoizedOnZero} 
                    stop={memoizedStopClock}
                    mode="receiver"
                />
            </div>

            {/** Skip */}
            {
                round?.mode === ChallengeMode.FIXED_TIME &&
                <div className={styles.skipCard}>
                    <div className={styles.timerHeader}>
                        <h4>Skip</h4>
                    </div>
                    <div className={styles.skipBatteryContainer}>
                        <SkipBattery bars={(round?.rule?.maxSkip || 0) - skipCount} />
                    </div>
                </div>
            }

        </div>
    )

    const gameDisplay = (
        <div className={styles.gameDisplay}>

            {/** Blackboard Game */}
            <div className={styles.blackboard}>
                {/* <MathJaxContext config={config} version={3}
                >
                    <div className={styles.mathDisplay}>
                        <MathJax dynamic>
                            <p>{"$$" + (questions && textToMathjax(questions[qIndex].question)) + '$$'}</p>
                        </MathJax>
                    </div>
                </MathJaxContext> */}
                {
                    questions && questions.length > 0 && questions[0].type == QuestionType.REGULAR ?
                    <div className={styles.mathDisplay}>
                        <motion.div animate={qControl}>
                            <TeX style={{ fontSize: 85, fontFamily: "Reddit Sans Variable" }}>
                                {questions && textToMathjax(questions[qIndex].question) }
                            </TeX>
                        </motion.div>
                    </div>
                    :
                    <div className={styles.fmDisplay}>
                        <p>{getPrompt(questions ? (questions[0].type || QuestionType.REGULAR) : QuestionType.REGULAR)}</p>
                        <div className={styles.fmNumber}>
                            {(questions && questions[qIndex].question.split('-')[0])}
                        </div>
                        <div className={styles.fmOptions}>
                            <div className={styles.fmOption}>{(questions && questions[qIndex].question.split('-')[1])}</div>
                            <div className={styles.fmOption}>{(questions && questions[qIndex].question.split('-')[2])}</div>
                            <div className={styles.fmOption}>{(questions && questions[qIndex].question.split('-')[3])}</div>
                            <div className={styles.fmOption}>{(questions && questions[qIndex].question.split('-')[4])}</div>
                        </div>
                    </div>
                }
                {/** Answer Indicator  */}
                <div className={styles.answerIndicator}>
                    <motion.div initial={{ opacity: 0 }} animate={rightControls}>
                        {
                            correct ?  
                            <CheckCircleOutlineSharpIcon
                                sx={{
                                    color: '#00EB96',
                                    fontSize: '180px',
                                    textAlign: 'center'
                                }}
                            /> : 
                            <CancelOutlinedIcon
                                sx={{
                                    color: '#CE3427',
                                    fontSize: '180px',
                                    textAlign: 'center'
                                }}
                            />
                        }
                    </motion.div>
                </div>
            </div>

            {/** Round Label */}
            <motion.div className={styles.roundLabel} animate={controls}
                //style={{ backgroundColor: background }}
            >
                <h4>{round?.rule?.roundName}</h4>
            </motion.div>

            {/** Performance Indicator */}
            {
                round?.mode === ChallengeMode.SUDDEN_DEATH &&
                <div style={{ marginTop: '7%' }}>
                    <PerformanceTimeline duration={round?.rule?.maxTime}
                        stop={stopClock}
                    />
                </div>
            }

        </div>
    )

    const timerAndCategory = (
        <div className={styles.timerLeaderboard}>
            {/** User Profile */}
            <Card sx={{ borderRadius: '8px' }}>
                <CardMedia
                    component="img"
                    alt="player image"
                    height="140"
                    sx={{ objectPosition: 'center' }}
                    image={round?.player && round.player.image ? round.player.image.url : defaultPic}
                />
                <CardContent sx={{ 
                    backgroundColor: "#349156", padding: '0 !important'
                    }}>
                    <div className={styles.cardMeta} >
                        <h3>{round?.player?.firstname}</h3>
                        <p>{round?.player?.grade} Grade</p>
                    </div>
                </CardContent>
            </Card>

            {/** Score card */}
            <div className={styles.scoreCard}>
                {/* <h4>CURRENT SCORE</h4> */}
                <h4>SCORE</h4>
                <div className={styles.score}>{scoreRef.current}</div>
            </div>


            {/** Performance metric Indicator*/}
            <div className={`${styles.scoreCard} ${styles.liveMetric}`}>
                <h4>LIVE PERFORMANCE METRIC</h4>
                <ReactSpeedometer
                    maxValue={1.5}
                    value={metricValue}
                    needleColor="red"
                    //segments={3}
                    customSegmentStops={[0, 0.3, 0.6, 0.9, 1.2, 1.5]}
                    fluidWidth
                    height={170}
                    ringWidth={25}
                    valueTextFontSize={'0px'}
                    segmentColors={[
                        "#C40700",    // Red
                        "#ff4500",    // Orange-Red
                        "#ffa500",    // Orange
                        "#ffd700",    // Gold
                        "#adff2f",    // Green-Yellow
                        "#4bc400"     // Green
                    ]}
                />
                <p className={styles.metricUnit} style={{ position: 'absolute', bottom: '6%' }}>
                    {`(Accurate Response/Second)`}
                </p>
            </div>

            {/** Performance metric value*/}
            <div className={styles.scoreCard}>
                <h4 className={styles.speedometerLabel}>ACCURATE RESPONSE SPEED</h4>
                <p className={styles.metricValue}>
                    {metricValue.toFixed(2)} 
                    
                </p>
                <span className={styles.metricUnit}>
                    {`(Accurate Response/Second)`}
                </span>
            </div>
        </div>
    )

    return(
        <>
        {/* <ScreenRecorder 
            name={'unnamed'} 
            ref={(r)=> screenRecorder.current = r}
            autoStart
            onStop={handleVideoProcessing}
        /> */}
        { 
            loading &&
            <WaitingForHost message='Processing Round'/>
        }
        <GameLayout>
            {/** Time Up Dialog */}
            <TimeUpDialog open={open} onClose={handleDialogClose} disableClose hideNext/>

            <div className={`${styles.main}`}>

                {/** Player Info */}
                { playerInfo }

                {/** Game Display */}
                { gameDisplay }

                {/** Timer and Leaderboard */}
                { timerAndCategory }

            </div>
        </GameLayout>
       </>
    )
}

export default ElemGameScreen;